import {
  alpha,
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  useTheme,
} from '@mui/material';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { ProductCommercialEsitmation } from '@/utils/calculator/Commercial';
import { ProductDSCREsitmation } from '@/utils/calculator/DSCR';
import { formatPrice } from '@/utils/helper';
import { ProductEsitmation } from '@/types/ProductType';

type ProductDetailProjectedIncomeProps = {
  estimation: ProductEsitmation;
};

export const ProductDetailProjectedIncome = ({
  estimation,
}: ProductDetailProjectedIncomeProps) => {
  const theme = useTheme();
  const dscrEstimation = estimation as ProductDSCREsitmation;
  const commercialEstimation = estimation as ProductCommercialEsitmation;

  if (
    !estimation ||
    (estimation.type !== USER_LOAN_PROGRAM.DSCR &&
      estimation.type !== USER_LOAN_PROGRAM.COMMERCIAL)
  )
    return null;

  const StackedFeeItem = ({
    label,
    value,
  }: {
    label: string;
    value: string | number;
  }) => (
    <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
      {/* Fee Label */}
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          fontSize: { xs: '0.875rem', sm: '0.9rem' },
          fontWeight: 500,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>

      {/* Spacer Bar */}
      <Box
        sx={{
          height: 1,
          background: alpha(theme.palette.text.primary, 0.12),
          mb: { xs: 0.75, sm: 1 },
          width: '100%',
        }}
      />

      {/* Fee Amount - Right Aligned */}
      <Typography
        variant="body1"
        sx={{
          textAlign: 'right',
          fontSize: { xs: '1rem', sm: '1.1rem' },
          fontWeight: 600,
          color: theme.palette.text.primary,
        }}
      >
        {typeof value === 'number' ? formatPrice(value) : value}
      </Typography>
    </Box>
  );

  return (
    <Card
      sx={{
        background: alpha(theme.palette.primary.main, 0.03),
        color: theme.palette.text.primary,
        borderRadius: 2,
        border: `1px solid ${theme.palette.greyShades.border1}`,
        boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <StackedFeeItem
          label="Monthly Rental Income"
          value={(estimation as any).rent || 0}
        />
        <StackedFeeItem
          label="Annual Rental Income"
          value={((estimation as any).rent || 0) * 12}
        />
        <StackedFeeItem
          label={
            estimation.type === USER_LOAN_PROGRAM.DSCR
              ? 'DSCR Ratio'
              : 'Commercial Ratio'
          }
          value={
            (estimation as any).dscr
              ? (estimation as any).dscr.toFixed(2)
              : 'N/A'
          }
        />
      </CardContent>
    </Card>
  );
};
